/**
 * The status of each policy in a consumer's plan, derived rather than stored.
 *
 * - `application-submitted`: the added quote, and an application was sent for that same quote;
 * - `added-quote-not-applied`: the added quote, which the consumer chose to keep without applying;
 * - `added-quote`: the added quote, otherwise;
 * - `existing-policy`: any other active policy (one the consumer already holds).
 *
 * Deactivated policies are left out. A policy whose quote is out of date (the consumer's details
 * changed since) is listed but not counted; `isPolicyCountedInPlan` tells which.
 *
 * None of these means coverage is in force: an added quote is a selection, and a submitted
 * application is not an issued policy. Applications whose quote is not the added quote (the policy
 * was removed or replaced after applying) are kept as sent and listed separately.
 */
import { findAddedQuoteFundingSourceId } from './addedQuote';

export type ConsumerPolicyStatus =
  | 'existing-policy'
  | 'added-quote'
  | 'added-quote-not-applied'
  | 'application-submitted';

export interface ConsumerPolicyEntry {
  fundingSourceId: string;
  policy: PolicyFundingSource;
  status: ConsumerPolicyStatus;
}

function isDeactivated(policy: PolicyFundingSource): boolean {
  const flag: unknown = policy.isDeactivated;
  return flag === true || flag === 'true';
}

export function deriveConsumerPolicies({
  ltcPolicies,
  selectedQuoteKey,
  continuedWithoutApplyingQuoteKey,
  applications,
}: {
  ltcPolicies: Record<string, PolicyFundingSource> | undefined;
  selectedQuoteKey: string | null;
  continuedWithoutApplyingQuoteKey: string | null;
  applications: readonly ConsumerApplicationSubmission[];
}): ConsumerPolicyEntry[] {
  const addedQuoteId = findAddedQuoteFundingSourceId(ltcPolicies, selectedQuoteKey);

  function addedQuoteStatus(): ConsumerPolicyStatus {
    if (applications.some(application => application.coverage.quoteKey === selectedQuoteKey)) {
      return 'application-submitted';
    }
    return continuedWithoutApplyingQuoteKey === selectedQuoteKey
      ? 'added-quote-not-applied'
      : 'added-quote';
  }

  return Object.entries(ltcPolicies ?? {})
    .filter(([, policy]) => !isDeactivated(policy))
    .map(([fundingSourceId, policy]) => ({
      fundingSourceId,
      policy,
      status: fundingSourceId === addedQuoteId ? addedQuoteStatus() : 'existing-policy',
    }));
}

/** Applications sent for a quote that is no longer the added quote; they stay exactly as sent. */
export function applicationsOutsideFundingPlan(
  applications: readonly ConsumerApplicationSubmission[],
  selectedQuoteKey: string | null,
): ConsumerApplicationSubmission[] {
  return applications.filter(application => application.coverage.quoteKey !== selectedQuoteKey);
}
